/**
 * @fileoverview Notification bell + dropdown.
 * Calls /api/notifications for the signed-in user's inbox, polls unread count.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, CheckCheck, Trash2, Megaphone, Activity, Trophy, Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { NotificationService } from '@/services/admin/admin-core.service'
import { FCM_FOREGROUND_EVENT } from '@/services/firebase/web-push'
import type { NotificationItem } from '@/types/admin-core.types'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

const CATEGORY_ICON: Record<string, LucideIcon> = {
  announcement: Megaphone,
  match: Activity,
  tournament: Trophy,
  system: Settings,
}

export function NotificationBell() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [count, setCount] = useState(0)
  const [items, setItems] = useState<NotificationItem[]>([])

  const loadCount = async () => {
    try {
      const r = await NotificationService.unreadCount()
      setCount(r.count ?? 0)
    } catch { /* ignore */ }
  }

  const loadItems = async () => {
    try {
      const r = await NotificationService.list({ limit: 20 })
      setItems(r?.data ?? [])
    } catch { /* ignore */ }
  }

  useEffect(() => {
    loadCount()
    const t = setInterval(loadCount, 30_000)
    return () => clearInterval(t)
  }, [])

  // Real-time bump on incoming FCM foreground message — registerWebPush
  // dispatches `fcm:foreground-message` whenever the backend pushes
  // something to this admin's browser. Increment immediately and re-sync
  // from the server so the count stays authoritative even if we miss an
  // event (page reload mid-push, multiple tabs, etc.).
  useEffect(() => {
    const onFcm = () => {
      setCount((c) => c + 1)
      loadCount()
      if (open) loadItems()
    }
    window.addEventListener(FCM_FOREGROUND_EVENT, onFcm)
    return () => window.removeEventListener(FCM_FOREGROUND_EVENT, onFcm)
  }, [open])

  useEffect(() => { if (open) loadItems() }, [open])

  // All four handlers below follow the same optimistic-with-rollback
  // pattern: snapshot the slice of state the action is about to mutate,
  // apply the change immediately so the UI feels instant, then await
  // the API. On rejection we restore the snapshot AND surface a toast
  // so the user knows the click didn't actually take effect — the
  // previous code silently swallowed errors and left the UI in a
  // permanently-divergent state until the next 30-second poll corrected
  // the count (and even then the per-item read-state stayed wrong).
  const markRead = async (n: NotificationItem) => {
    if (!n.is_read) {
      const prevItems = items
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, is_read: true } : x))
      try {
        await NotificationService.markRead(n.id)
        // Re-sync count from server — single source of truth.
        loadCount()
      } catch {
        setItems(prevItems)
        // Pull the real count back in case the optimistic side-effect
        // left things skewed elsewhere.
        loadCount()
        toast.error('Could not mark notification as read')
      }
    }
    // Navigation is independent of the read-state update — if the user
    // tapped a notification with a link, take them there regardless of
    // whether the read-flip succeeded. Matches the previous behaviour.
    if (n.link) {
      setOpen(false)
      navigate(n.link)
    }
  }

  const markAllRead = async () => {
    const prevItems = items
    const prevCount = count
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })))
    setCount(0)
    try {
      await NotificationService.markAllRead()
    } catch {
      setItems(prevItems)
      setCount(prevCount)
      // Background reconcile to be safe — the server may have processed
      // a partial mark-all-read before erroring.
      loadCount()
      toast.error('Could not mark all notifications as read')
    }
  }

  const remove = async (id: string) => {
    const prevItems = items
    setItems((prev) => prev.filter((x) => x.id !== id))
    try {
      await NotificationService.remove(id)
      loadCount()
    } catch {
      setItems(prevItems)
      loadCount()
      toast.error('Could not delete notification')
    }
  }

  const clearAll = async () => {
    if (!confirm('Delete all notifications? This cannot be undone.')) return
    const prevItems = items
    const prevCount = count
    setItems([])
    setCount(0)
    try {
      await NotificationService.removeAll(false)
    } catch {
      setItems(prevItems)
      setCount(prevCount)
      loadCount()
      toast.error('Could not delete all notifications')
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}>
          <Bell className="h-5 w-5" aria-hidden="true" />
          {count > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 min-w-5 px-1 text-[10px] bg-red-500 hover:bg-red-500 text-white border-0">
              {count > 99 ? '99+' : count}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[70vh] overflow-y-auto p-0">
        <div className="sticky top-0 z-10 bg-popover/95 backdrop-blur border-b p-3 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold">Notifications</p>
          <div className="flex items-center gap-1">
            {count > 0 && (
              <Button size="sm" variant="ghost" onClick={markAllRead} className="h-7 text-xs">
                <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all read
              </Button>
            )}
            {items.length > 0 && (
              <Button size="sm" variant="ghost" onClick={clearAll} className="h-7 text-xs text-red-500 hover:text-red-600">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete all
              </Button>
            )}
          </div>
        </div>

        {items.length === 0 ? (
          <div className="py-12 text-center">
            <Bell className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No notifications yet</p>
          </div>
        ) : (
          <ul className="divide-y">
            {items.map((n) => {
              const Icon = CATEGORY_ICON[n.category ?? ''] ?? Bell
              return (
                <li
                  key={n.id}
                  className={cn('p-3 flex items-start gap-3 cursor-pointer hover:bg-accent transition-colors', !n.is_read && 'bg-emerald-50/40 dark:bg-emerald-950/10')}
                  onClick={() => markRead(n)}
                >
                  <div className={cn('h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0', !n.is_read ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground')}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-sm', !n.is_read && 'font-semibold')}>{n.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(n.id) }}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        <div className="sticky bottom-0 bg-popover/95 backdrop-blur border-t p-2">
          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => { setOpen(false); navigate('/notifications') }}>
            View all notifications
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
